import type { ChangeEvent } from "react";
import { TIMING_REQUIREMENTS, type AccessWindowDraft, type JobTimingDraft, type TimingRequirement } from "../lib/jobForm";
import styles from "./JobTimingFields.module.css";

interface JobTimingFieldsProps {
  /** Timing block of the job draft. */
  value: JobTimingDraft;
  /** Receives the whole timing block on every edit; this component holds no state. */
  onChange: (next: JobTimingDraft) => void;
}

function newAccessWindow(): AccessWindowDraft {
  return { id: crypto.randomUUID(), availableOn: "", startTime: "09:00", endTime: "12:00" };
}

/**
 * Requester timing constraints, kept separate from vendor scheduling. Presentational
 * only: all state changes flow up through `onChange`.
 *
 * @param {JobTimingFieldsProps} props - Timing draft and its change callback.
 */
export function JobTimingFields({ value, onChange }: JobTimingFieldsProps) {
  const patch = (next: Partial<JobTimingDraft>): void => onChange({ ...value, ...next });

  const updateWindow = (id: string, next: Partial<AccessWindowDraft>): void =>
    patch({
      accessWindows: value.accessWindows.map((window) =>
        window.id === id ? { ...window, ...next } : window,
      ),
    });

  const handleRequirementChange = (event: ChangeEvent<HTMLSelectElement>): void =>
    patch({ requirement: event.target.value as TimingRequirement });
  const handleCompletionChange = (event: ChangeEvent<HTMLInputElement>): void =>
    patch({ requestedCompletionOn: event.target.value });
  const handleReasonChange = (event: ChangeEvent<HTMLTextAreaElement>): void =>
    patch({ reason: event.target.value });
  const handleNotBeforeChange = (event: ChangeEvent<HTMLInputElement>): void =>
    patch({ requestedNotBeforeOn: event.target.value });
  const handleTimezoneChange = (event: ChangeEvent<HTMLInputElement>): void =>
    patch({ timezone: event.target.value });
  const handleAddWindow = (): void => patch({ accessWindows: [...value.accessWindows, newAccessWindow()] });
  const handleRemoveWindow = (id: string): void =>
    patch({ accessWindows: value.accessWindows.filter((candidate) => candidate.id !== id) });

  return (
    <fieldset className={styles.root}>
      <legend className={styles.legend}>Timing and availability</legend>
      <span className={styles.note}>
        Tell us what you need. This is a request constraint, not a confirmed booking.
      </span>

      <div className={styles.requirementRow}>
        <label className={styles.field}>
          <span className={styles.label}>When does this work need to be completed?</span>
          <select
            className={styles.select}
            name="timingRequirement"
            value={value.requirement}
            onChange={handleRequirementChange}
          >
            {TIMING_REQUIREMENTS.map((requirement) => (
              <option key={requirement} value={requirement}>
                {requirement}
              </option>
            ))}
          </select>
        </label>

        {value.requirement !== "No specific date" && (
          <div className={styles.conditional}>
            <label className={styles.field}>
              <span className={styles.label}>Requested completion date</span>
              <input
                className={styles.input}
                name="timingCompletion"
                type="date"
                value={value.requestedCompletionOn}
                onChange={handleCompletionChange}
              />
            </label>
          </div>
        )}

        {value.requirement !== "No specific date" && (
          <label className={`${styles.field} ${styles.reasonField}`}>
            <span className={styles.label}>
              Timing reason{value.requirement === "Must be completed by" ? " (required)" : " (optional)"}
            </span>
            <textarea
              className={styles.textarea}
              name="timingReason"
              rows={2}
              value={value.reason}
              onChange={handleReasonChange}
            />
          </label>
        )}
      </div>

      <label className={styles.field}>
        <span className={styles.label}>Do not start before (optional)</span>
        <input
          className={styles.input}
          name="timingNotBefore"
          type="date"
          value={value.requestedNotBeforeOn}
          onChange={handleNotBeforeChange}
        />
      </label>

      <div className={styles.windowHeader}>
        <div className={styles.windowHeading}>
          <span className={styles.label}>Access availability</span>
          <span className={styles.note}>Available times allow attendance; they do not promise completion.</span>
        </div>
        <button type="button" className={styles.addButton} onClick={handleAddWindow}>
          Add window
        </button>
      </div>

      {value.accessWindows.length > 0 && (
        <label className={styles.field}>
          <span className={styles.label}>Property time zone</span>
          <input
            className={styles.input}
            name="timingTimezone"
            maxLength={100}
            value={value.timezone}
            onChange={handleTimezoneChange}
          />
        </label>
      )}

      {value.accessWindows.map((window, index) => (
        <WindowRow
          key={window.id}
          index={index}
          window={window}
          onPatch={updateWindow}
          onRemove={handleRemoveWindow}
        />
      ))}
    </fieldset>
  );
}

interface WindowRowProps {
  /** Position in the list, used only for the human-facing row number. */
  index: number;
  window: AccessWindowDraft;
  onPatch: (id: string, next: Partial<AccessWindowDraft>) => void;
  onRemove: (id: string) => void;
}

/** One date + from/to access window, edited by id so reordering cannot cross values. */
function WindowRow({ index, window, onPatch, onRemove }: WindowRowProps) {
  const handleDateChange = (event: ChangeEvent<HTMLInputElement>): void =>
    onPatch(window.id, { availableOn: event.target.value });
  const handleStartChange = (event: ChangeEvent<HTMLInputElement>): void =>
    onPatch(window.id, { startTime: event.target.value });
  const handleEndChange = (event: ChangeEvent<HTMLInputElement>): void =>
    onPatch(window.id, { endTime: event.target.value });
  const handleRemove = (): void => onRemove(window.id);

  return (
    <div className={styles.window}>
      <label className={styles.field}>
        <span className={styles.windowLabel}>Access window {index + 1} — date</span>
        <input
          className={styles.input}
          name={`windowDate-${window.id}`}
          type="date"
          value={window.availableOn}
          onChange={handleDateChange}
        />
      </label>
      <label className={styles.field}>
        <span className={styles.windowLabel}>From</span>
        <input
          className={styles.input}
          name={`windowStart-${window.id}`}
          type="time"
          value={window.startTime}
          onChange={handleStartChange}
        />
      </label>
      <label className={styles.field}>
        <span className={styles.windowLabel}>To</span>
        <input
          className={styles.input}
          name={`windowEnd-${window.id}`}
          type="time"
          value={window.endTime}
          onChange={handleEndChange}
        />
      </label>
      <button type="button" className={styles.removeButton} onClick={handleRemove}>
        Remove
      </button>
    </div>
  );
}
